import React, {useEffect} from "react";
import {
  Table,
  InputNumber,
  Input,
  Button,
  Tag,
  Popconfirm,
  Tooltip,
  Checkbox,
  message,
  Popover,
  Divider,
} from "antd";
import { DeleteOutlined, EditOutlined, CopyOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const TimesheetTable = ({
  rows,
  setRows,
  weekDates,
  dateKeys,
  unsavedChanges,
  setUnsavedChanges,
  openAddModal,
  openUploadModal,
  onClonePreviousWeek, 
  setDeletedTimesheetIds,
  onSave,
  onCancel
}) => {
  // const filteredDates = weekDates.filter((d) => d.day() !== 0);
  const filteredDates = weekDates
  // const filteredKeys = dateKeys.filter((_, i) => weekDates[i].day() !== 0);
  const filteredKeys = dateKeys

  useEffect(() => {
    console.log("TimesheetTable rendered with rows:", rows);

  }, [rows]);

  useEffect(() => {
    if (unsavedChanges) {
      // Auto-clear selection because user is editing
      const updated = rows.map(r => ({ 
        ...r, 
        isSelectedForApproval: false 
      }));
      setRows(updated);
    }
  }, [unsavedChanges]);

  const resetAllSelections = () => {
    const updated = rows.map(r => ({
      ...r,
      isSelectedForApproval: false
    }));
    setRows(updated);
  };
  
  
  const handleHourChange = (recordIndex, dateKey, value) => {
    resetAllSelections();
    const updated = [...rows];
  
    // If the field is cleared → remove hours entirely
    if (value === null || value === undefined || value === "") {
      if (updated[recordIndex].entries[dateKey]) {
        delete updated[recordIndex].entries[dateKey].hours;
        // If notes also not added → remove entry object itself
        if (
          !updated[recordIndex].entries[dateKey].notes ||
          updated[recordIndex].entries[dateKey].notes.trim() === ""
        ) {
          delete updated[recordIndex].entries[dateKey];
        }
      }
    } else {
      // normal hour update
      const hours = Number(value);
      updated[recordIndex].entries[dateKey] = {
        ...(updated[recordIndex].entries[dateKey] || {}),
        hours,
      };
    }
  
    setRows(updated);
    setUnsavedChanges(true);
  };
  

  // const handleRowSelection = (projectId, checked) => {
  //   const updated = [...rows];
  //   const idx = updated.findIndex((r) => r.projectId === projectId);
  //   if (idx > -1) {
  //     updated[idx].isSelectedForApproval = checked;
  //     setRows(updated);
  //     setUnsavedChanges(true);
  //   }
  // };
  const handleRowSelection = (assignmentId, checked) => {
    if (unsavedChanges) {
      message.warning("Please Save or Cancel before selecting rows.");
      return;
    }
  
    const updated = [...rows];
    const idx = updated.findIndex((r) => r.assignmentId === assignmentId);
  
    if (idx > -1) {
      updated[idx].isSelectedForApproval = checked;
      setRows(updated);
      setUnsavedChanges(false);
    }
  };
  
  const handleBulkDelete = () => {
    console.log('Initiating bulk delete...');
    console.log('Current rows:', rows);
    const selected = rows.filter(
      (r) => r.isSelectedForApproval && r.status === "Draft"
    );
    // const selectedDraftRows = rows.filter(
    //   (r) => r.isSelectedForApproval && r.status === "Draft"
    // );
    
    if (selected.length === 0) {
      return message.error("Please select at least one row to delete.");
    }
  
    const nonDraft = selected.filter((r) => r.status !== "Draft");
    if (nonDraft.length > 0) {
      return message.error("Only Draft rows can be deleted.");
    }
  
    // Collect deleted IDs before state change
    const deletedIds = selected
      .map((r) => r.timesheetId)
      .filter(Boolean);
  
    // Update state: remove deleted rows
    setRows((prev) => prev.filter((r) => !r.isSelectedForApproval));
  
    // Update global deleted list state
    setDeletedTimesheetIds((prev) => [...prev, ...deletedIds]);
  
    // Call save with only deleted IDs
    onSave({
      deletedIds,
      updatedRows: []
    });
  };
  
  const handleBulkApproval = () => {
    const selected = rows.filter(
      (r) => r.isSelectedForApproval && r.status === "Draft"
    );  
    if (selected.length === 0) {
      return message.error("Please select at least one row to submit.");
    }
  
    if (selected.some((r) => r.status !== "Draft")) {
      return message.error("Only Draft rows can be submitted for approval.");
    }
  
    // Update selected rows to Pending Approval
    const updated = rows.map((r) =>
      r.isSelectedForApproval ? { ...r, status: "Pending Approval" } : r
    );
  
    setRows(updated);
  
    // Only these rows need saving
    const updatedRows = updated.filter((r) => r.isSelectedForApproval);
  
    onSave({
      deletedIds: [],
      updatedRows
    });
  
    message.success("Rows submitted for approval.");
  };
  
      
  const handleNoteChange = (recordIndex, dateKey, value) => {
    resetAllSelections();
    const updated = [...rows];
    const entry = updated[recordIndex].entries[dateKey] || {};
  
    const notes = value; // keep as-is, allow multiline / no trim
  
    // If notes cleared and no hours → delete full entry
    if ((!notes || notes === "") && !entry.hours) {
      delete updated[recordIndex].entries[dateKey];
    } else {
      // otherwise update (add notes or keep empty notes when hours exist)
      updated[recordIndex].entries[dateKey] = {
        ...entry,
        notes,
      };
    }
  
    setRows(updated);
    setUnsavedChanges(true);
  };
  

  const handleDelete = (projectId) => {
    const rowToDelete = rows.find((r) => r.projectId === projectId);
    // store timesheet id if exists
    if (rowToDelete?.timesheetId) {
      setDeletedTimesheetIds(prev => [...prev, rowToDelete.timesheetId]);
    }
    // remove from UI
    const updated = rows.filter((r) => r.projectId !== projectId);
    setRows(updated);
    setUnsavedChanges(true);
    message.success("Project entry removed");
  };
  

  const handleApprovalCheckbox = (checked, projectId) => {
    const updated = [...rows];
    const idx = updated.findIndex((r) => r.projectId === projectId);
    if (idx > -1) {
      updated[idx].isSelectedForApproval = checked;
      setRows(updated);
      setUnsavedChanges(true);
    }
  };

  const canEditRow = (record) => {
    return record.status === "Draft" || record.status === "Rejected"; // ONLY draft rows editable
  };
  
  const columns = [
    {
      title: "",
      key: "selectRow",
      width: 40,
      fixed: "left",
      render: (_, record) => (
        <Checkbox
          disabled={unsavedChanges || !canEditRow(record)}
          checked={record.isSelectedForApproval}
          onChange={(e) =>
            handleRowSelection(record.assignmentId, e.target.checked)
          }
        />
      ),
    },
    
    {
      title: "Project",
      dataIndex: "projectName",
      key: "projectName",
      fixed: "left",
      width: 120,
      render: (text, record) => (
        <a
          href={`/record/Project/${record.projectId}`}
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: "1.3em",
            fontSize: 12,
            color: "#1677ff",
            textDecoration: "underline",
            cursor: "pointer",
          }}
          target="_blank"    // optional: open in new tab
          rel="noopener noreferrer"
        >
        <>
          {record.projectName} — {record.assignmentName}{" "} - {record?.billing_type}{" "}
          <span style={{ fontSize: 11, color: "#888" }}>
            ({dayjs(record.assignmentStart).format("DD MMM YYYY")} → {dayjs(record.assignmentEnd).format("DD MMM YYYY")})
          </span>
        </>
        </a>
      )
      
    },
    // {
    //   title: "Billable",
    //   dataIndex: "isBillable",
    //   key: "isBillable",
    //   fixed: "left",
    //   width: 70,
    //   align: "center",
    //   render: (val) => (
    //     <Tag color={val ? "green" : "volcano"} style={{ margin: 0 }}>
    //       {val ? "Yes" : "No"}
    //     </Tag>
    //   ),
    // },
    ...filteredDates.map((d, i) => ({
      title: d.format("ddd DD"),
      key: filteredKeys[i],
      width: 110,
      align: "center",
      render: (_, record, index) => {
        const entry = record.entries?.[filteredKeys[i]] || {};
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              borderLeft: "1px solid #f0f0f0",
              paddingLeft: 4,
            }}
          >
            <InputNumber

              min={0}
              max={24}
              step={0.5}
              size="small"
              placeholder="hrs"
              value={entry.hours}
              disabled={!canEditRow(record)}
              onChange={(val) => handleHourChange(index, filteredKeys[i], val)}
              onKeyPress={(e) => {
                if (!/[0-9.]$/.test(e.key)) e.preventDefault();
              }}
              style={{ width: "100%" }}
              controls={false}
            />

          <Popover
            trigger="click"
            placement="topLeft"
            content={
              <Input.TextArea
                autoFocus
                placeholder="Enter detailed notes"
                value={entry.notes || ""}
                autoSize={{ minRows: 3, maxRows: 6 }}
                onChange={(e) => handleNoteChange(index, filteredKeys[i], e.target.value)}
                style={{ width: 230 }}
                disabled={!canEditRow(record)}
              />
            }
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #d9d9d9",
                borderRadius: 4,
                padding: "0 4px",
                height: 26,
                background: ["Pending Approval", "Approved"].includes(record.status)
                  ? "#f5f5f5"
                  : "#fafafa",
                cursor: ["Pending Approval", "Approved"].includes(record.status)
                  ? "not-allowed"
                  : "pointer",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: 90,        // FIX: never expands, independent from flex container
                maxWidth: 90,
                minWidth: 90,
                flexShrink: 0,     // FIX: ensures it never shrinks or grows
                boxSizing: "border-box",
                opacity: ["Pending Approval", "Approved"].includes(record.status)
                  ? 0.6
                  : 1,
              }}
              title={entry.notes} // hover shows full note
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  maxWidth: "calc(100% - 16px)", // leave space for icon
                }}
              >
                {entry.notes || "Add notes..."}
              </span>
              <EditOutlined style={{ fontSize: 12, color: "#888" }} />
            </div>
          </Popover>


          </div>
        );
      },
    })),
    {
      title: "Total(hrs)",
      key: "total",
      // fixed: "right",
      width: 70,
      align: "center",
      render: (_, record) =>
        Object.values(record.entries || {}).reduce(
          (sum, e) => sum + (e.hours || 0),
          0
        ),
    },
    {
      title: "Status",
      key: "status",
      width: 90,
      fixed: "right",
      align: "center",
      render: (_, record) => {
        console.log("Record Status:", record.status);
        const status = record.status || "Draft";
      
        // Match colors with header
        const color =
          status === "Approved"
            ? "green"
            : status === "Pending Approval"
            ? "orange"
            : status === "Rejected"
            ? "red"
            : "blue"; // Draft
      
        return (
          <Tag
            color={color}
            style={{
              fontSize: 10,
              padding: "0 6px",
              fontWeight: 500,
            }}
          >
            {status}
          </Tag>
        );
      },
      
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   width: 80,
    //   fixed: "right",
    //   align: "center",
    //   render: (_, record) => (
    //     <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
    //       <Tooltip title="Delete Project">
    //         <Popconfirm
    //           title="Delete this project?"
    //           okText="Yes"
    //           cancelText="No"
    //           onConfirm={() => handleDelete(record.projectId)}
    //         >
    //           <DeleteOutlined
    //             style={{
    //               color: canEditRow(record) ? "red" : "#ccc",
    //               fontSize: 14,
    //               cursor: canEditRow(record) ? "pointer" : "not-allowed",
    //             }}
    //           />

    //         </Popconfirm>
    //       </Tooltip>
    //       <Tooltip title="Select for Approval">
    //         <Checkbox
    //           size="small"
    //           disabled={!canEditRow(record)}
    //           checked={
    //             ["Pending Approval", "Approved", "Rejected"].includes(
    //               record.status
    //             ) || record.isSelectedForApproval
    //           }
    //           onChange={(e) =>
    //             handleApprovalCheckbox(e.target.checked, record.projectId)
    //           }
    //         />
    //       </Tooltip>
    //     </div>
    //   ),
    // },
  ];

  // --- Totals ---
  const totalBillable = rows
    .filter((r) => r.isBillable)
    .reduce(
      (sum, r) =>
        sum +
        Object.values(r.entries || {}).reduce(
          (s, e) => s + (e.hours || 0),
          0
        ),
      0
    );

  const totalNonBillable = rows
    .filter((r) => !r.isBillable)
    .reduce(
      (sum, r) =>
        sum +
        Object.values(r.entries || {}).reduce(
          (s, e) => s + (e.hours || 0),
          0
        ),
      0
    );

  const dayTotals = filteredKeys.map((key) =>
    rows.reduce((sum, r) => sum + (r.entries?.[key]?.hours || 0), 0)
  );

  const totalHours = totalBillable + totalNonBillable;

  return (
    <div style={{ background: "#fff", padding: 12, borderRadius: 8 }}>
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <Button size="small" onClick={openUploadModal}>
          Upload CSV
        </Button>

        <Button size="small" icon={<CopyOutlined />} onClick={onClonePreviousWeek}>
          Clone Previous Week
        </Button>

        <Popconfirm
          title="Are you sure you want to delete the selected rows?"
          okText="Yes"
          cancelText="No"
          onConfirm={handleBulkDelete}
        >
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
          >
            Delete
          </Button>
        </Popconfirm>



        <Button
          size="small"
          type="primary"
          onClick={handleBulkApproval}
        >
          Submit for Approval
        </Button>

        <Button size="small" type="primary" onClick={openAddModal}>
          Add Project
        </Button>
      </div>


      <Table
        size="small"
        bordered
        columns={columns}
        dataSource={rows}
        rowKey={(r) => r.assignmentId}
        pagination={false}
        scroll={{ x: "max-content", y: 360 }}
        tableLayout="fixed"
        summary={() => {
          const grandDayTotal = dayTotals.reduce((sum, h) => sum + h, 0);

          return (
            <Table.Summary fixed>
              <Table.Summary.Row>

                {/* Checkbox */}
                <Table.Summary.Cell index={0} />

                {/* Project label */}
                <Table.Summary.Cell index={1}>
                  <b>Day Totals:</b>
                </Table.Summary.Cell>

                {/* Date columns */}
                {filteredDates.map((_, i) => (
                  <Table.Summary.Cell index={i + 2} key={i} align="center">
                    <b>{dayTotals[i].toFixed(1)} hrs</b>
                  </Table.Summary.Cell>
                ))}

                {/* 🔥 TOTAL COLUMN (this was missing) */}
                <Table.Summary.Cell
                  index={filteredDates.length + 2}
                  align="center"
                >
                  <b style={{ color: "#1677ff" }}>
                    {grandDayTotal.toFixed(1)} hrs
                  </b>
                </Table.Summary.Cell>

                {/* Status column */}
                <Table.Summary.Cell index={filteredDates.length + 3} />

              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />

    </div>
  );
};

export default TimesheetTable;
